<script setup lang="ts">
import { computed } from 'vue';
import { CustomFormsFeaturesData } from '@nova/core';

import { useCustomFieldProps } from '@satellite/composables/useCustomFieldProps';
import { useCustomFieldValidation } from '@satellite/composables/useCustomFieldValidation';

import type { CustomFieldProps } from '../types';
import CustomFieldInputBase from '../CustomFieldInputBase.vue';

/**
 * Action field type - feature selection radio group
 */
const {
  hideDetails = 'auto',
  displayLabel = true,
  required = undefined,
  ...props
} = defineProps<CustomFieldProps>();

const emit = defineEmits<{
  'update:model-value': [value: string];
  /** @deprecated Use update:model-value instead */
  input: [value: string];
  /** @deprecated Never consumed; kept only for backward compatibility. */
  action: [];
}>();

const { normalizedProps } = useCustomFieldProps(() => ({
  ...props,
  hideDetails,
  displayLabel,
  required
}));
const { rules } = useCustomFieldValidation(
  computed(() => ({ ...props.field, required: normalizedProps.required }))
);

function featureData(feature: string) {
  return CustomFormsFeaturesData[feature as keyof typeof CustomFormsFeaturesData];
}

function handleInput(value: string) {
  emit('action');
  emit('update:model-value', value);
  emit('input', value);
}
</script>

<template>
  <custom-field-input-base :field="field" v-bind="normalizedProps">
    <v-radio-group
      :data-testid="testId"
      color="primary"
      density="comfortable"
      :hide-details="normalizedProps.hideDetails"
      :model-value="modelValue"
      :readonly="normalizedProps.readOnly"
      :rules="rules"
      @update:model-value="handleInput">
      <v-radio
        v-for="feature of field.dropDownValues"
        :key="feature"
        :value="feature"
        :id="feature">
        <template #label>
          <span class="d-flex flex-column">
            <span class="typography-body-sm-bold">{{ featureData(feature)?.title }}</span>
            <small class="typography-body-sm">{{ featureData(feature)?.description }}</small>
          </span>
        </template>
      </v-radio>
    </v-radio-group>
  </custom-field-input-base>
</template>

<style scoped lang="scss">
:deep(.v-radio:not(:last-child)) {
  margin-bottom: $spacing-5;
}
</style>
